import { useState } from "react";
import { X } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CardAssignee } from "@kandoo/shared";
import { api } from "../api/client";
import { Avatar } from "./Avatar";

interface CardAssigneesProps {
  boardId: string;
  cardId: string;
  assigned: CardAssignee[];
  canEdit: boolean;
}

/** Card modal "Assignees" section: shows assigned members and a picker. */
export function CardAssignees({
  boardId,
  cardId,
  assigned,
  canEdit,
}: CardAssigneesProps) {
  const qc = useQueryClient();
  const membersQ = useQuery({
    queryKey: ["board-members", boardId],
    queryFn: () => api.members.list(boardId),
    enabled: canEdit,
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["card", cardId] });
    qc.invalidateQueries({ queryKey: ["board", boardId] });
  };
  const assign = useMutation({
    mutationFn: (userId: string) => api.assignees.assign(cardId, userId),
    onSuccess: invalidate,
  });
  const unassign = useMutation({
    mutationFn: (userId: string) => api.assignees.unassign(cardId, userId),
    onSuccess: invalidate,
  });

  const [picking, setPicking] = useState(false);
  const assignedIds = new Set(assigned.map((a) => a.userId));
  const members = membersQ.data ?? [];

  return (
    <section className="mt-6">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="font-medium">Assignees</h2>
        {canEdit && (
          <button
            type="button"
            onClick={() => setPicking((v) => !v)}
            className="rounded bg-slate-100 px-2 py-1 text-sm text-slate-700 hover:bg-slate-200"
          >
            {picking ? "Done" : "Assign"}
          </button>
        )}
      </div>

      {assigned.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {assigned.map((a) => (
            <span
              key={a.userId}
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 py-0.5 pl-0.5 pr-2 text-sm"
            >
              <Avatar userId={a.userId} name={a.displayName || a.username} />
              {a.displayName || a.username}
              {canEdit && (
                <button
                  type="button"
                  onClick={() => unassign.mutate(a.userId)}
                  className="text-slate-400 hover:text-red-600"
                  title="Unassign"
                >
                  <X size={12} />
                </button>
              )}
            </span>
          ))}
        </div>
      ) : (
        !picking && <p className="text-sm text-slate-500">No assignees.</p>
      )}

      {picking && canEdit && (
        <div className="mt-2 rounded-lg border border-slate-200 bg-[var(--surface)] p-2">
          {members.map((m) => {
            const on = assignedIds.has(m.userId);
            return (
              <button
                key={m.userId}
                type="button"
                onClick={() =>
                  on ? unassign.mutate(m.userId) : assign.mutate(m.userId)
                }
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-slate-50"
              >
                <input type="checkbox" checked={on} readOnly />
                <Avatar userId={m.userId} name={m.displayName || m.username} />
                <span className="flex-1">{m.displayName || m.username}</span>
                <span className="text-xs text-slate-400">@{m.username}</span>
              </button>
            );
          })}
          {members.length === 0 && (
            <p className="px-2 py-1 text-sm text-slate-500">No board members.</p>
          )}
        </div>
      )}
    </section>
  );
}
